import { useTranslation } from '../i18n';
import type { SortKey, SortSpec } from './CatalogFilters';
import { defaultDirFor } from './CatalogFilters';
import { COL, ROW_FRAME } from './WorkRowLayout';

interface Props {
    sort: SortSpec;
    onChange: (next: SortSpec) => void;
}

export function WorkRowHeader({ sort, onChange }: Props) {
    const t = useTranslation();
    const select = (key: SortKey) => {
        if (sort.key === key) {
            onChange({ key, dir: sort.dir === 'asc' ? 'desc' : 'asc' });
        } else {
            onChange({ key, dir: defaultDirFor(key) });
        }
    };

    return (
        <div
            class={`${ROW_FRAME} sticky top-0 z-10 border-rule-strong bg-surface-raised font-mono text-[10.5px] font-normal uppercase tracking-[0.14em] text-fg-muted`}
        >
            <SortableCell
                label={t('catalogRow.colSource')}
                col={COL.source}
                active={sort.key === 'source'}
                dir={sort.dir}
                onClick={() => select('source')}
            />
            <SortableCell
                label={t('catalogRow.colName')}
                col={COL.name}
                active={sort.key === 'name'}
                dir={sort.dir}
                onClick={() => select('name')}
            />
            <SortableCell
                label={t('catalogRow.colAuthor')}
                col={COL.author}
                active={sort.key === 'author'}
                dir={sort.dir}
                onClick={() => select('author')}
            />
            <SortableCell
                label={t('catalogRow.colKind')}
                col={COL.kind}
                active={sort.key === 'kind'}
                dir={sort.dir}
                onClick={() => select('kind')}
            />
            <span class={COL.install}>{t('catalogRow.colInstall')}</span>
            <span class={COL.mirror}>{t('catalogRow.colMirror')}</span>
            <SortableCell
                label={t('catalogRow.colLastUpdate')}
                col={COL.last}
                active={sort.key === 'changed'}
                dir={sort.dir}
                onClick={() => select('changed')}
                align="right"
            />
        </div>
    );
}

interface CellProps {
    label: string;
    col: string;
    active: boolean;
    dir: 'asc' | 'desc';
    onClick: () => void;
    align?: 'left' | 'right';
}

function SortableCell({ label, col, active, dir, onClick, align = 'left' }: CellProps) {
    const arrow = active ? (dir === 'asc' ? '↑' : '↓') : '';
    return (
        <button
            type="button"
            onClick={onClick}
            class={`${col} ${align === 'right' ? 'text-right' : 'text-left'} transition-colors hover:text-fg-primary ${
                active ? 'text-accent' : ''
            }`}
        >
            <span>{label}</span>
            {active && <span class="ml-1">{arrow}</span>}
        </button>
    );
}
